import { LegalPageLayout } from '@/components/layout/LegalPageLayout';
import { LEGAL_SUITE_VERSION, LEGAL_SUITE_LAST_UPDATED_LABEL } from '@/constants/legalSuite';

export default function TermsAndConditions() {
  return (
    <LegalPageLayout
      title="Términos y Condiciones"
      lastUpdated={LEGAL_SUITE_LAST_UPDATED_LABEL}
      version={LEGAL_SUITE_VERSION}
    >
      <nav className="bg-muted/50 rounded-lg p-4 sm:p-6 mb-8 border">
        <p className="font-semibold text-foreground mb-3">Contenido</p>
        <ol className="!text-sm space-y-1">
          <li><a href="#objeto">Objeto del servicio</a></li>
          <li><a href="#uso-permitido">Uso permitido</a></li>
          <li><a href="#responsabilidades">Responsabilidades del usuario</a></li>
          <li><a href="#suscripciones">Planes, suscripciones y pagos</a></li>
          <li><a href="#limitacion">Limitación de responsabilidad</a></li>
          <li><a href="#propiedad">Propiedad intelectual</a></li>
          <li><a href="#suspension">Suspensión o cancelación de acceso</a></li>
          <li><a href="#cambios-servicio">Cambios al servicio</a></li>
          <li><a href="#contacto">Contacto</a></li>
        </ol>
      </nav>

      <p>
        Estos Términos y Condiciones regulan el uso de la plataforma <strong>FOCO — Tu Dinero, Tu Poder</strong>. Al
        registrarte o usar el servicio, aceptas cumplir con los presentes términos en su totalidad. Si no estás de
        acuerdo con ellos, no debes usar el servicio.
      </p>

      <h2 id="objeto">1. Objeto del servicio</h2>
      <p>
        FOCO es una plataforma de gestión financiera personal diseñada para ayudar a los usuarios a organizar
        sus finanzas, registrar transacciones, establecer metas de ahorro, gestionar deudas y obtener
        recomendaciones personalizadas basadas en su comportamiento financiero.
      </p>
      <p>
        El servicio incluye herramientas de seguimiento de presupuesto, cálculo del Puntaje FOCO,
        método Bola de Nieve para deudas, reportes financieros y un asistente de inteligencia artificial.
        Hay un <strong>plan gratuito</strong> con funcionalidades limitadas y un <strong>plan Premium de pago</strong>{' '}
        con acceso ampliado según la app en cada momento.
      </p>

      <h2 id="uso-permitido">2. Uso permitido</h2>
      <p>El usuario se compromete a:</p>
      <ul>
        <li>Utilizar la plataforma únicamente para fines de gestión financiera personal legítima.</li>
        <li>Proporcionar información veraz y actualizada al registrarse y usar el servicio.</li>
        <li>No utilizar la plataforma para actividades ilegales, fraudulentas o que violen derechos de terceros.</li>
        <li>No intentar acceder a datos o cuentas de otros usuarios.</li>
        <li>No realizar ingeniería inversa, copiar o distribuir el software de la plataforma.</li>
      </ul>

      <h2 id="responsabilidades">3. Responsabilidades del usuario</h2>
      <p>El usuario es responsable de:</p>
      <ul>
        <li>Mantener la confidencialidad de sus credenciales de acceso (correo electrónico y contraseña).</li>
        <li>La veracidad y exactitud de los datos financieros que registra en la plataforma.</li>
        <li>Todas las actividades realizadas desde su cuenta.</li>
        <li>Notificar inmediatamente cualquier uso no autorizado de su cuenta.</li>
      </ul>

      <h2 id="suscripciones">4. Planes, suscripciones y pagos</h2>
      <h3 className="text-lg font-semibold mt-4">4.1 Planes</h3>
      <ul>
        <li>
          <strong>Plan gratuito:</strong> acceso básico con restricciones (por ejemplo, límite de importación de PDF,
          módulos bloqueados como metas, bola de nieve, chat IA y alertas por correo de ciertas funciones).
        </li>
        <li>
          <strong>Plan Premium:</strong> acceso a funciones Premium mediante suscripción mensual recurrente, según
          el precio publicado en la app y en Stripe al momento de la contratación.
        </li>
      </ul>
      <h3 className="text-lg font-semibold mt-4">4.2 Pagos con Stripe</h3>
      <p>
        Los pagos se procesan con <strong>Stripe</strong>. Al contratar Premium aceptas también los términos de Stripe
        en Checkout o en el Portal de cliente. FOCO no almacena el número completo de tu tarjeta.
      </p>
      <h3 className="text-lg font-semibold mt-4">4.3 Renovación, cancelación e impago</h3>
      <ul>
        <li>Las suscripciones se renuevan automáticamente hasta que las canceles en el Portal de Stripe (Configuración).</li>
        <li>La cancelación suele aplicar al final del periodo ya pagado; no hay reembolso prorrateado salvo ley imperativa o promoción expresa.</li>
        <li>Si un cobro falla, podemos limitar el acceso Premium hasta que actualices el método de pago.</li>
        <li>Los precios se muestran en Stripe antes de confirmar (típicamente en USD).</li>
      </ul>
      <h3 className="text-lg font-semibold mt-4">4.4 Acceso por asesor</h3>
      <p>
        La suspensión de tu cuenta por tu asesor es independiente de Stripe: puedes tener Premium activo y no entrar a
        la app si tu mentor restringió el acceso. Los <strong>administradores</strong> pueden usar Premium sin pagar; no
        es transferible.
      </p>

      <h2 id="limitacion">5. Limitación de responsabilidad</h2>
      <p>
        FOCO es una herramienta de organización y seguimiento financiero. <strong>No constituye asesoría financiera,
        contable, fiscal ni legal profesional.</strong> Las recomendaciones generadas por el asistente de inteligencia
        artificial son orientativas y no sustituyen el consejo de un profesional certificado.
      </p>
      <p>
        No nos hacemos responsables de decisiones financieras tomadas con base en la información proporcionada por la
        plataforma. El usuario acepta que el uso de la plataforma es bajo su propio riesgo.
      </p>
      <p>
        En la medida máxima permitida por la ley aplicable, el responsable (Erick S Meono / FOCO — Tu Dinero Tu Poder)
        no será
        responsable por daños indirectos, incidentales, especiales, consecuentes o punitivos (incluyendo pérdida de
        beneficios, datos o negocio). Cuando la ley no permita la exclusión total, la responsabilidad máxima del
        responsable frente a ti por cualquier reclamación derivada o relacionada con el uso del servicio se limitará
        al monto que hayas pagado por el servicio en los doce (12) meses anteriores a la reclamación, o, si no has
        pagado, a cien dólares estadounidenses (USD 100) o su equivalente en moneda local.
      </p>

      <h2 id="propiedad">6. Propiedad intelectual</h2>
      <p>
        Todos los elementos de la plataforma FOCO, incluyendo pero no limitado a: diseño, código fuente,
        logotipos, marcas, textos, gráficos e interfaces, son propiedad de Tu Dinero, Tu Poder y están
        protegidos por las leyes de propiedad intelectual aplicables.
      </p>
      <p>
        Los datos financieros registrados por el usuario son de su propiedad. FOCO únicamente los procesa
        para brindar el servicio contratado.
      </p>

      <h2 id="suspension">7. Suspensión o cancelación de acceso</h2>
      <p>Nos reservamos el derecho de suspender o cancelar el acceso de un usuario en los siguientes casos:</p>
      <ul>
        <li>Violación de estos Términos y Condiciones.</li>
        <li>Uso indebido o abusivo de la plataforma.</li>
        <li>Comportamiento que ponga en riesgo la seguridad o integridad del servicio.</li>
        <li>Inactividad prolongada de la cuenta (según políticas vigentes).</li>
      </ul>
      <p>
        En caso de suspensión, el usuario será notificado con la razón correspondiente. El usuario podrá
        solicitar la reactivación contactando al equipo de soporte.
      </p>

      <h2 id="cambios-servicio">8. Cambios al servicio</h2>
      <p>
        Nos reservamos el derecho de modificar, actualizar o discontinuar funcionalidades de la plataforma
        en cualquier momento. Los cambios significativos serán comunicados a los usuarios con anticipación
        razonable a través de la plataforma o por correo electrónico.
      </p>
      <p>
        Estos Términos y Condiciones pueden ser actualizados periódicamente. La fecha de última actualización
        se indica al inicio del documento. El uso continuado del servicio tras una modificación implica
        la aceptación de los nuevos términos.
      </p>

      <h2 id="contacto">9. Contacto y ley aplicable</h2>
      <p>Para consultas relacionadas con estos términos, puedes contactarnos en:</p>
      <ul>
        <li>
          <strong>Correo electrónico:</strong> info@premium-advisor.com
        </li>
        <li>
          <strong>Sitio web:</strong> www.tudinerotupoder.com
        </li>
      </ul>
      <p>
        <strong>Responsable:</strong> Erick S Meono, Managing Member (miembro gestor) — producto FOCO — Tu Dinero Tu
        Poder.
        <br />
        <strong>Domicilio:</strong> 9850 S Maryland Pkwy Ste A-5557, Las Vegas, Nevada 89183, Estados Unidos de América.
      </p>
      <p>
        Estos términos se rigen por las leyes de <strong>Costa Rica</strong> (o, si se determina otra jurisdicción
        para el responsable, por las leyes del lugar donde este tenga su domicilio principal), sin perjuicio de las
        normas de derecho internacional privado que resulten aplicables.
      </p>
    </LegalPageLayout>
  );
}
